import { LOCALSTORAGE_KEY_BH_TRACKING_CONTEXT } from '../../../constants'

const LOW_PRIORITY_CONTEXTS = ['product', 'wishlist', 'cart', 'mini-cart', 'checkout']
const DIVIDER = '|'

/**
 *
 * @param {{sku: string, pageContext: string, pageId?: number, widgetContext?: string}|undefined} storedContext
 * @param {string} context
 * @param {string=} widgetContext
 * @returns {boolean} `true` if context is priority over storedContext
 */
const isPriorityContext = (storedContext, context, widgetContext) => {
	if (!storedContext) return true
	if (!context) return false

	if (LOW_PRIORITY_CONTEXTS.includes(context)) {
		// low priority context overrides high priority context only if it has widgetContext set
		// or if stored context hasn't a widgetContext set
		// exception for product page, that has always priority over other low priority pages
		if (storedContext.pageContext === 'product') {
			if (widgetContext) {
				return true
			} else {
				return false
			}
		} else {
			if (
				widgetContext ||
				(LOW_PRIORITY_CONTEXTS.includes(storedContext.pageContext) && !storedContext?.widgetContext)
			) {
				return true
			} else {
				return false
			}
		}
	}

	return true
}

/**
 * @description Update LOCALSTORAGE_KEY_BH_TRACKING_CONTEXT value in localStorage
 * @param {object} params
 * @param {object} params.product - Product object
 * @param {string} params.pageContext - pageContext: eg. `product`, `cart`, `checkout`, `wishlist`
 * @param {string|number=} params.pageId - pageId: eg. `1234` or `search`
 * @param {string=} params.widgetContext - widgetContext: eg. `cross-sells`, `product-carousel`
 * @param {function} params.getLocalStorageByGroup - getLocalStorageByGroup: function to get localStorage by group
 * @param {function} params.setLocalStorageByGroup - setLocalStorageByGroup: function to set localStorage by group
 * @returns {void}
 */
export const updateBhTrackingContextValue = ({
	product,
	pageContext,
	pageId,
	widgetContext,
	getLocalStorageByGroup = () => {},
	setLocalStorageByGroup = () => {},
}) => {
	const value = getLocalStorageByGroup(LOCALSTORAGE_KEY_BH_TRACKING_CONTEXT)

	if (!value || !value[product.sku]) {
		setLocalStorageByGroup(
			LOCALSTORAGE_KEY_BH_TRACKING_CONTEXT,
			{
				...value,
				[product.sku]: {
					sku: product.sku,
					pageContext,
					// if pageId is set and it's equal to pageContext, we know it's forced
					// and we do not add it to the context
					pageId: pageId && pageId !== pageContext ? pageId : undefined,
					widgetContext,
				},
			},
			// expire time 1 day
			new Date(new Date().getTime() + 24 * 60 * 60 * 1000)
		)
	} else {
		if (isPriorityContext(value[product.sku], pageContext, widgetContext)) {
			setLocalStorageByGroup(
				LOCALSTORAGE_KEY_BH_TRACKING_CONTEXT,
				{
					...value,
					[product.sku]: {
						sku: product.sku,
						pageContext,
						// if pageId is set and it's equal to pageContext, we know it's forced
						// and we do not add it to the context
						pageId: pageId && pageId !== pageContext ? pageId : undefined,
						widgetContext,
					},
				},
				// expire time 1 day
				new Date(new Date().getTime() + 24 * 60 * 60 * 1000)
			)
		}
	}

	return
}

/**
 * @description Helper function to join pageContext and widgetContext
 * @param {object} params
 * @param {string} params.pageContext - pageContext: eg. `product`, `cart`, `checkout`, `wishlist`
 * @param {string|number=} params.pageId - pageId: eg. `1234` or `search`
 * @param {string=} params.widgetContext - widgetContext: eg. `cross-sells`, `product-carousel`
 * @param {boolean=} params.enablePageId - enablePageId: if true, pageId will be added to the context
 * @returns {string | undefined} `pageContext|pageId|widgetContext`
 */
export const getContextValue = ({ pageContext, pageId, widgetContext, enablePageId = true }) => {
	let context = undefined
	if (pageContext) {
		// page context should always be set
		context = pageContext
		// if pageId is set and it's equal to pageContext, we know it's forced
		// and we don't need to add it to the context
		if (enablePageId && pageId && pageId !== pageContext) {
			context += `${DIVIDER}${pageId}`
		}
		if (widgetContext) {
			context += `${DIVIDER}${widgetContext}`
		}
	}
	return context
}

/**
 * @description Get context value from localStorage by sku
 * @param {object} params
 * @param {string} params.sku - Product sku
 * @param {string} params.pageContext - pageContext: eg. `product`, `cart`, `checkout`, `wishlist`
 * @param {string|number=} params.pageId - pageId: eg. `1234` or `search`
 * @param {string=} params.widgetContext - widgetContext: eg. `cross-sells`, `product-carousel`
 * @param {function} params.getLocalStorageByGroup - getLocalStorageByGroup: function to get localStorage by group
 * @param {boolean=} params.enablePageId - enablePageId: if true, pageId will be added to the context
 * @returns {string | undefined} `pageContext|pageId|widgetContext`
 */
export const getContextValueBySku = ({
	sku,
	pageContext,
	pageId,
	widgetContext,
	getLocalStorageByGroup = () => {},
	enablePageId = true,
}) => {
	try {
		const value = getLocalStorageByGroup(LOCALSTORAGE_KEY_BH_TRACKING_CONTEXT)

		if (isPriorityContext(value?.[sku], pageContext, widgetContext)) {
			return getContextValue({ pageContext, pageId, widgetContext, enablePageId })
		} else {
			return getContextValue({
				pageContext: value?.[sku]?.pageContext,
				pageId: value?.[sku]?.pageId,
				widgetContext: value?.[sku]?.widgetContext,
				enablePageId,
			})
		}
	} catch (e) {
		return undefined
	}
}
